import { CartItem } from 'src/types/cart';
import { FavoriteProduct } from 'src/types/favorite';

export interface CartMappableProduct {
  productKey: string;
  productId: number;
  catalogId: number;
  categoryId: number;
  isFormulary: boolean;
}

export interface FavoriteMappableProduct {
  productId: number;
  catalogId: number;
  categoryId: number;
  isFormulary: boolean;
  supplierId: number;
}

export interface ProductSelectionQty {
  selected: boolean;
  qty: number;
}

/** Same `${catalogId}-${categoryId}-${productId}` key the product mappers give each row. */
export const getCartItemProductKey = ({ catalogId, categoryId, productId }: CartItem): string =>
  `${catalogId}-${categoryId}-${productId}`;

/** Deselects one product (drops its entry) and keeps every other selection. */
export const removeProductSelection = <T>(
  productSelections: Record<string, T>,
  productKey: string
): Record<string, T> => {
  if (!(productKey in productSelections)) {
    return productSelections;
  }
  const next = { ...productSelections };
  delete next[productKey];
  return next;
};

export const mapSelectionsToCartItems =<T extends CartMappableProduct>(
  productSelections: Record<string, ProductSelectionQty>,
  products: T[]
): CartItem[] => {
  const selectedItemNumbers = Object.entries(productSelections)
    .filter(([, selection]) => selection.selected)
    .map(([productKey, selection]) => ({ productKey, quantity: selection.qty }));

  const selectedItems = products.filter((product) =>
    selectedItemNumbers.some((item) => item.productKey === product.productKey)
  );

  return selectedItems.map((item) => ({
    productId: item.productId,
    catalogId: item.catalogId,
    categoryId: item.categoryId,
    quantity:
      selectedItemNumbers.find((selected) => selected.productKey === item.productKey)?.quantity ||
      0,
    isCompanySpecified: item.isFormulary,
  }));
};

export const mapProductsToFavoriteItems = <T extends FavoriteMappableProduct>(
  products: T[]
): FavoriteProduct[] =>
  products.map((product) => ({
    productId: product.productId,
    catalogId: product.catalogId,
    categoryId: product.categoryId,
    isCompanySpecifiedItem: product.isFormulary,
    offlineSupplierId: product.supplierId,
  }));
